import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Users, 
  TrendingUp, 
  Target, 
  DollarSign,
  Download,
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Flame,
  Thermometer,
  Snowflake,
  Building2,
  BarChart3,
  PieChart,
  RefreshCw
} from 'lucide-react';

const ROLE_LABELS = {
  dg: 'Directeur Général',
  drh: 'DRH',
  rh_manager: 'Responsable RH',
  manager: 'Manager',
  consultant: 'Consultant',
  other: 'Autre'
};

const NEED_LABELS = {
  trombinoscope: 'Trombinoscope',
  organigramme: 'Organigramme',
  profiles: 'Gestion profils',
  teams: 'Équipes/Départements',
  analytics: 'Analytics'
};

const SIZE_LABELS = {
  '10': '≤10',
  '50': '11-50',
  '100': '51-100',
  '500': '101-500',
  '+500': '500+'
};

const MATURITY_CONFIG = {
  hot: { label: 'Chaud', icon: Flame, color: 'bg-red-100 text-red-700' },
  warm: { label: 'Tiède', icon: Thermometer, color: 'bg-amber-100 text-amber-700' },
  cold: { label: 'Froid', icon: Snowflake, color: 'bg-blue-100 text-blue-700' }
};

export default function MarketingDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filters, setFilters] = useState({
    role: '',
    recommendedPack: '',
    maturityLevel: '',
    primaryNeed: '',
    employeeCount: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  useEffect(() => {
    fetchStats();
    fetchLeads();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [filters, pagination.page]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/leads/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', 15);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/leads?${params.toString()}`);
      setLeads(response.data.leads);
      setPagination(prev => ({
        ...prev,
        totalPages: response.data.totalPages,
        total: response.data.total
      }));
    } catch (error) {
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/leads/export?format=csv&${params.toString()}`);
      
      // Create download
      const blob = new Blob([response.data.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}/status`, null, { params: { status: newStatus } });
      toast.success('Statut mis à jour');
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const clearFilters = () => {
    setFilters({
      role: '',
      recommendedPack: '',
      maturityLevel: '',
      primaryNeed: '',
      employeeCount: '',
      status: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const StatCard = ({ title, value, icon: Icon, description, color = 'bg-webtech-blue' }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          <div className={`w-14 h-14 ${color} rounded-xl flex items-center justify-center`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout title="Marketing & Leads">
      <div className="space-y-6" data-testid="marketing-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Marketing & Leads
            </h1>
            <p className="text-slate-600">
              Suivi et segmentation des prospects
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { fetchStats(); fetchLeads(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={handleExport} className="bg-webtech-blue hover:bg-webtech-blue-hover text-white">
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Leads" 
              value={stats.total} 
              icon={Users}
            />
            <StatCard 
              title="Leads Chauds" 
              value={stats.byMaturity?.hot || 0} 
              icon={Flame}
              color="bg-red-500"
              description="Score ≥70"
            />
            <StatCard 
              title="Taux de conversion" 
              value={`${stats.conversionRate}%`} 
              icon={TrendingUp}
              color="bg-green-500"
            />
            <StatCard 
              title="Clients" 
              value={stats.converted} 
              icon={DollarSign}
              color="bg-emerald-500"
            />
          </div>
        )}

        {/* Charts Row */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* By Pack */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-4 h-4" />
                  Par Pack Recommandé
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.byPack || {}).map(([pack, count]) => (
                    <div key={pack} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          pack === 'enterprise' ? 'bg-purple-500' :
                          pack === 'pro' ? 'bg-webtech-blue' : 'bg-slate-400'
                        }`} />
                        <span className="text-sm text-slate-600 capitalize">{pack}</span>
                      </div>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By Role */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Par Rôle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.byRole || {}).slice(0, 5).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{ROLE_LABELS[role] || role}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By Size */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Par Taille Entreprise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(stats.bySize || {}).map(([size, count]) => (
                    <div key={size} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{SIZE_LABELS[size] || size} employés</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <CardTitle>Liste des Leads</CardTitle>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <Select
                  value={filters.maturityLevel || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, maturityLevel: v === 'all' ? '' : v })}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Maturité" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="hot">🔥 Chaud</SelectItem>
                    <SelectItem value="warm">🌡️ Tiède</SelectItem>
                    <SelectItem value="cold">❄️ Froid</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.recommendedPack || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, recommendedPack: v === 'all' ? '' : v })}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Pack" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="free">STARTER</SelectItem>
                    <SelectItem value="pro">BUSINESS</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={filters.status || 'all'}
                  onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="qualified">Qualifié</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <Filter className="w-4 h-4 mr-1" />
                  Effacer
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Taille</TableHead>
                    <TableHead>Pack</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Promo</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-webtech-blue" />
                      </TableCell>
                    </TableRow>
                  ) : leads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-slate-500">
                        Aucun lead trouvé
                      </TableCell>
                    </TableRow>
                  ) : (
                    leads.map((lead) => {
                      const maturity = MATURITY_CONFIG[lead.maturityLevel] || MATURITY_CONFIG.cold;
                      const MaturityIcon = maturity.icon;
                      
                      return (
                        <TableRow key={lead.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">
                                {lead.firstName} {lead.lastName}
                              </p>
                              <p className="text-sm text-slate-500">{lead.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {ROLE_LABELS[lead.role] || lead.role}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{SIZE_LABELS[lead.employeeCount] || lead.employeeCount}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              lead.recommendedPack === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                              lead.recommendedPack === 'pro' ? 'bg-webtech-blue/10 text-webtech-blue' :
                              'bg-slate-100 text-slate-600'
                            }>
                              {lead.recommendedPack}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge className={maturity.color}>
                                <MaturityIcon className="w-3 h-3 mr-1" />
                                {lead.maturityScore}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            {lead.promotionApplied ? (
                              <Badge variant="outline" className="bg-webtech-yellow/10 text-amber-700 border-webtech-yellow">
                                -{lead.promotionDiscount}%
                              </Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={lead.status}
                              onValueChange={(v) => updateLeadStatus(lead.id, v)}
                            >
                              <SelectTrigger className="h-8 w-[110px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="prospect">Prospect</SelectItem>
                                <SelectItem value="lead">Lead</SelectItem>
                                <SelectItem value="qualified">Qualifié</SelectItem>
                                <SelectItem value="client">Client</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(lead.createdAt).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setSelectedLead(lead)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-600">
                  {pagination.total} lead{pagination.total > 1 ? 's' : ''} • Page {pagination.page} / {pagination.totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead Detail Dialog */}
        <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
          <DialogContent className="max-w-2xl">
            {selectedLead && (
              <>
                <DialogHeader>
                  <DialogTitle>
                    Détail du Lead - {selectedLead.firstName} {selectedLead.lastName}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{selectedLead.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Téléphone</p>
                      <p className="font-medium">{selectedLead.phone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Entreprise</p>
                      <p className="font-medium">{selectedLead.companyName || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Rôle</p>
                      <p className="font-medium">{ROLE_LABELS[selectedLead.role] || selectedLead.role}</p>
                    </div>
                  </div>

                  {/* Questionnaire Responses */}
                  <div>
                    <h4 className="font-semibold mb-3">Réponses au questionnaire</h4>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                      <p><strong>Nombre d'employés:</strong> {SIZE_LABELS[selectedLead.employeeCount]}</p>
                      <p><strong>Besoin principal:</strong> {NEED_LABELS[selectedLead.primaryNeed]}</p>
                      <p><strong>Annuaire existant:</strong> {selectedLead.hasDirectory ? 'Oui' : 'Non'}</p>
                      <p><strong>Personnalisation:</strong> {selectedLead.customizationLevel}</p>
                      <p><strong>Délai:</strong> {selectedLead.implementationDelay}</p>
                    </div>
                  </div>

                  {/* Scoring & Recommendation */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-webtech-blue/5 rounded-lg p-4">
                      <p className="text-sm text-slate-500 mb-1">Score de maturité</p>
                      <p className="text-3xl font-bold text-webtech-blue">{selectedLead.maturityScore}</p>
                      <Badge className={MATURITY_CONFIG[selectedLead.maturityLevel]?.color}>
                        {MATURITY_CONFIG[selectedLead.maturityLevel]?.label}
                      </Badge>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm text-slate-500 mb-1">Pack recommandé</p>
                      <p className="text-xl font-bold capitalize">{selectedLead.recommendedPack}</p>
                      <p className="text-sm text-slate-600">
                        ${selectedLead.discountedPrice}/mois
                        {selectedLead.promotionApplied && (
                          <span className="text-green-600 ml-2">(-{selectedLead.promotionDiscount}%)</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* UTM */}
                  {(selectedLead.utmSource || selectedLead.utmMedium || selectedLead.utmCampaign) && (
                    <div>
                      <h4 className="font-semibold mb-2">Source UTM</h4>
                      <div className="flex gap-2">
                        {selectedLead.utmSource && <Badge variant="outline">Source: {selectedLead.utmSource}</Badge>}
                        {selectedLead.utmMedium && <Badge variant="outline">Medium: {selectedLead.utmMedium}</Badge>}
                        {selectedLead.utmCampaign && <Badge variant="outline">Campaign: {selectedLead.utmCampaign}</Badge>}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
